/**
 * A restrained browser chrome for real product screenshots.
 *
 * Every shot in public/hazribook/shots is 1500×940, and the intrinsic
 * width/height are set explicitly so the browser reserves the right box
 * before the image arrives. Without them these are the largest elements on
 * the page and each one would shift the layout as it loads, which is the
 * single easiest way to fail Cumulative Layout Shift.
 *
 * Paths must be absolute. A relative src breaks at /hazribook when the URL
 * has no trailing slash.
 */
export function BrowserFrame({
  src,
  alt,
  label,
  caption,
  priority,
  className = "",
}: {
  src: string;
  alt: string;
  /** Shown in the fake address bar. A product name, not a URL. */
  label?: string;
  caption?: string;
  /** Set on the one image likely to be the Largest Contentful Paint. */
  priority?: boolean;
  className?: string;
}) {
  return (
    <figure className={`m-0 ${className}`}>
      <div className="overflow-hidden rounded-xl border border-white/10 bg-ink-900/70 shadow-glow backdrop-blur-xl">
        <div className="flex items-center gap-2 border-b border-white/5 px-3 py-2">
          <span className="size-2 rounded-full bg-white/15" />
          <span className="size-2 rounded-full bg-white/15" />
          <span className="size-2 rounded-full bg-white/15" />
          {label ? (
            <span className="ml-2 truncate rounded bg-white/[0.05] px-2 py-0.5 text-[10px] text-white/45">
              {label}
            </span>
          ) : null}
        </div>
        <img
          src={src}
          alt={alt}
          width={1500}
          height={940}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          {...(priority ? { fetchPriority: "high" as const } : {})}
          className="block h-auto w-full"
        />
      </div>
      {caption ? (
        <figcaption className="mt-2 text-center text-[11px] text-white/40">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
